/* Query Board insights (admin + Query Team): repeat-problem alert + per-person stats. */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { openParcelQuery } from "@/components/ParcelQueryDialog";

const fmtHrs = (h) => (h == null ? "—" : h < 24 ? `${h}h` : `${(h / 24).toFixed(1)}d`);

export const RepeatProblemAlert = ({ alerts }) => {
  const [open, setOpen] = useState(false);
  if (!alerts?.length) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="pq-repeat-alert">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left font-semibold"
        data-testid="pq-repeat-alert-toggle">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        Repeat problem: {alerts.length} product{alerts.length > 1 ? "s" : ""} in 3+ queries in the last 7 days
        <span className="font-normal truncate">— {alerts.slice(0, 3).map((a) => `${a.product} (${a.count})`).join(", ")}</span>
        {open ? <ChevronUp className="ml-auto w-4 h-4" /> : <ChevronDown className="ml-auto w-4 h-4" />}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid="pq-repeat-list">
          {alerts.map((a, i) => (
            <div key={a.product} className="rounded bg-white/70 p-1.5" data-testid={`pq-repeat-item-${i}`}>
              <b>{a.product}</b> <span className="text-amber-700">· {a.count} queries</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {a.queries.map((q) => (
                  <button key={q.id} type="button" onClick={() => openParcelQuery(q.row_id)}
                    className="rounded border border-amber-200 bg-white px-1.5 py-0.5 hover:bg-amber-100" data-testid={`pq-repeat-q-${q.id}`}>
                    {q.customer_name} · {q.code} · {q.type} · {(q.created_at || "").slice(0, 10)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const QueryTeamStats = ({ rows }) => {
  if (!rows) return null;
  return (
    <div className="rounded border border-slate-200 p-2" data-testid="pq-team-stats">
      <div className="text-[11px] font-semibold text-slate-600 mb-1">Query team stats (follows the range filter)</div>
      {rows.length ? (
        <table className="w-full text-[11px]">
          <thead className="text-slate-500"><tr>
            <th className="text-left p-1">Person</th><th className="text-right p-1">Raised</th>
            <th className="text-right p-1">Solved</th><th className="text-right p-1">Avg solve time</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name} className="border-t" data-testid={`pq-team-row-${i}`}>
                <td className="p-1 font-medium">{r.name}</td>
                <td className="p-1 text-right" data-testid={`pq-team-raised-${i}`}>{r.raised}</td>
                <td className="p-1 text-right" data-testid={`pq-team-solved-${i}`}>{r.solved}</td>
                <td className="p-1 text-right" data-testid={`pq-team-avg-${i}`}>{fmtHrs(r.avg_solve_hours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div className="text-[11px] text-slate-400">No data</div>}
    </div>
  );
};

const Satisfaction = ({ s }) => {
  if (!s || !(s.up + s.down)) return <div className="mt-1 text-[11px] text-slate-400" data-testid="pq-satisfaction-empty">No partner ratings in this period</div>;
  const pct = Math.round((s.up / (s.up + s.down)) * 100);
  return (
    <div className="mt-2 border-t pt-1.5 text-[11px]" data-testid="pq-satisfaction">
      <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-700">Partner satisfaction
        <span className="inline-flex items-center gap-0.5 text-emerald-700" data-testid="pq-satisfaction-up"><ThumbsUp className="h-3 w-3" />{s.up}</span>
        <span className="inline-flex items-center gap-0.5 text-rose-600" data-testid="pq-satisfaction-down"><ThumbsDown className="h-3 w-3" />{s.down}</span>
        <span className={pct >= 80 ? "text-emerald-700" : "text-rose-600"}>{pct}% happy</span>
        {s.people.map((p) => <span key={p.name} className="rounded bg-slate-100 px-1.5 font-normal">{p.name}: {p.up} / {p.down}</span>)}</div>
      {s.recent_down.map((x, i) => <div key={i} className="mt-0.5 text-rose-700" data-testid={`pq-satisfaction-down-${i}`}>
        <b>{x.customer_name}</b> ({x.code}) · solved by {x.name || "?"}: “{x.reason}”</div>)}
    </div>
  );
};

export const PartnerReplyTimes = () => {
  const [weeks, setWeeks] = useState(4);
  const [rows, setRows] = useState(null);
  const [goal, setGoal] = useState(2);
  const [edit, setEdit] = useState(null);
  const [sat, setSat] = useState(null);
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } })();
  const load = () => axios.get(`${API}/order-register/queries/partner-reply-times`, { params: { weeks }, headers: getAuthHeader() })
    .then((r) => { setRows(r.data.rows); setGoal(r.data.goal_hours ?? 2); setSat(r.data.satisfaction); }).catch(() => setRows([]));
  useEffect(() => { load(); }, [weeks]);
  const saveGoal = () => axios.put(`${API}/order-register/queries/reply-goal`, { hours: edit }, { headers: getAuthHeader() })
    .then(() => { setEdit(null); load(); }).catch((e) => alert(e.response?.data?.detail || "Save failed"));
  if (rows === null) return null;
  return (
    <div className="rounded border border-fuchsia-200 p-2" data-testid="pq-reply-times">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-fuchsia-800">Partner query reply times (first message → first team reply)
        <span className="rounded bg-fuchsia-100 px-1.5 py-0.5 font-normal" data-testid="pq-reply-goal">Goal: {fmtHrs(goal)}
          {isAdmin && edit === null && <button type="button" className="ml-1 text-indigo-600 underline" onClick={() => setEdit(String(goal))} data-testid="pq-reply-goal-edit">change</button>}</span>
        {edit !== null && <span className="flex items-center gap-1 font-normal">
          <input value={edit} onChange={(e) => setEdit(e.target.value)} className="h-6 w-14 rounded border px-1" data-testid="pq-reply-goal-input" /> hours
          <button type="button" onClick={saveGoal} className="rounded bg-fuchsia-700 px-2 py-0.5 text-white" data-testid="pq-reply-goal-save">Save</button>
          <button type="button" onClick={() => setEdit(null)} className="text-slate-500">Cancel</button></span>}
        <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="ml-auto h-6 rounded border px-1 text-[11px] font-normal" data-testid="pq-reply-times-weeks">
          {[1, 4, 8, 12].map((w) => <option key={w} value={w}>Last {w} week{w > 1 ? "s" : ""}</option>)}</select></div>
      {rows.length ? (
        <table className="w-full text-[11px]">
          <thead className="text-slate-500"><tr><th className="text-left p-1">Week</th><th className="text-left p-1">Team member</th>
            <th className="text-right p-1">Replies</th><th className="text-right p-1">Avg first reply</th><th className="text-right p-1">Missed goal</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={`${r.week}-${r.name}`} className={`border-t ${r.over_goal ? "bg-rose-50" : ""}`} data-testid={`pq-reply-row-${i}`}>
              <td className="p-1">{r.week}</td><td className={`p-1 font-medium ${r.over_goal ? "text-rose-700" : ""}`}>{r.name}</td>
              <td className="p-1 text-right">{r.replies}</td>
              <td className={`p-1 text-right font-semibold ${r.over_goal ? "text-rose-600" : "text-emerald-700"}`}>{fmtHrs(r.avg_hours)}</td>
              <td className={`p-1 text-right ${r.missed ? "font-semibold text-rose-600" : "text-slate-400"}`} data-testid={`pq-reply-missed-${i}`}>{r.missed}</td></tr>))}</tbody>
        </table>
      ) : <div className="text-[11px] text-slate-400">No partner queries answered in this period</div>}
      <Satisfaction s={sat} />
    </div>
  );
};
